import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { playFlipSound, playWinFanfare } from '../utils/soundEffects';
import { getSettings } from '../utils/db';

const { FiGift, FiAward, FiStar } = FiIcons;

const FlipToRevealCard = ({ reward, onRevealed, compact = false }) => {
  const [isFlipped, setIsFlipped] = useState(false);

  const handleTap = () => {
    if (isFlipped) return;
    setIsFlipped(true);

    const settings = getSettings();
    if (settings.soundEnabled !== false) {
      playFlipSound();
      setTimeout(() => {
        playWinFanfare();
      }, 300);
    }

    // Add a slight delay before triggering the next step so user can see the reward
    setTimeout(() => {
      if (onRevealed) onRevealed();
    }, 2000);
  };

  return (
    <div className={`w-full ${compact ? 'max-w-[260px]' : 'max-w-[320px] sm:max-w-sm'} mx-auto aspect-[3/4] perspective-1000 cursor-pointer select-none`} onClick={handleTap}>
      <motion.div
        className="w-full h-full relative preserve-3d duration-700 ease-in-out"
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
      >
        {/* Front of Card (Unscratched) */}
        <div className="absolute inset-0 backface-hidden w-full h-full rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 border-2 border-slate-600 shadow-2xl flex flex-col items-center justify-center p-4 sm:p-6 text-center overflow-hidden">
          <div className="absolute inset-0 opacity-20 bg-[url('https://images.unsplash.com/photo-1557672172-298e090bd0f1?auto=format&fit=crop&w=600&q=80')] bg-cover bg-center mix-blend-overlay"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <motion.div 
              animate={{ scale: [1, 1.1, 1] }} 
              transition={{ repeat: Infinity, duration: 2 }}
              className={`${compact ? 'w-16 h-16 mb-3' : 'w-20 h-20 mb-6'} rounded-full bg-gradient-to-tr from-amber-400 to-orange-500 flex items-center justify-center shadow-[0_0_30px_rgba(245,158,11,0.5)]`}
            >
              <SafeIcon icon={FiStar} className={`${compact ? 'text-2xl' : 'text-4xl'} text-white`} />
            </motion.div>
            <h3 className={`${compact ? 'text-lg' : 'text-2xl'} font-bold text-white mb-1 uppercase tracking-wider`}>Tap to Reveal</h3>
            <p className="text-slate-300 text-xs sm:text-sm">Discover your surprise reward!</p>
          </div>
        </div>

        {/* Back of Card (Revealed Reward) */}
        <div className="absolute inset-0 backface-hidden rotate-y-180 w-full h-full rounded-2xl bg-gradient-to-br from-amber-100 to-orange-200 border-2 border-amber-300 shadow-2xl flex flex-col items-center justify-center p-4 sm:p-6 text-center overflow-hidden">
          <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1513151233558-d860c5398176?auto=format&fit=crop&w=600&q=80')] bg-cover bg-center"></div>
          
          <div className="relative z-10 flex flex-col items-center">
            <div className={`${compact ? 'w-16 h-16 mb-3' : 'w-22 h-22 mb-5'} rounded-full bg-white shadow-xl flex items-center justify-center`}>
              {reward.includes('Luck') || reward.includes('Try') ? (
                <SafeIcon icon={FiAward} className={`${compact ? 'text-3xl' : 'text-5xl'} text-slate-400`} />
              ) : (
                <SafeIcon icon={FiGift} className={`${compact ? 'text-3xl' : 'text-5xl'} text-orange-500`} />
              )}
            </div>
            <h4 className="text-xs font-semibold text-orange-600 uppercase tracking-widest mb-1">You Won</h4>
            <h2 className={`${compact ? 'text-xl' : 'text-2xl sm:text-3xl'} font-extrabold text-slate-800 leading-tight px-2`}>
              {reward}
            </h2>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default FlipToRevealCard;